import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import dayjs from 'dayjs';
import { AssessmentsService } from './assessments.service';
import { AssessmentPage } from '../models/assessments.interface';
import { AssessmentStatusPipe } from '../pipes/assessment-status.pipe';
import { AssessmentCollaborationState, AssessmentColumnEnum, AssessmentInviteeStatusEnum, AssessmentQuestionTypeEnum } from 'src/app/api/models/assessments/assessment.enum';
import { AssessmentCustomFieldDropdown, AssessmentCustomFieldText, AssessmentEntity, AssessmentResourceId } from 'src/app/api/models/assessments/assessment.interface';
import { ReviewDateDiffPipe } from '../ai-assessments-page/ai-assessment-review-date-dialog/review-date-diff.pipe';
import { AiAssessmentTemplateNamePipe } from '../pipes/ai-assessment-template-name.pipe';
import { EmployeesService } from 'src/app/employees/state/employees.service';
import { SystemsQuery } from 'src/app/systems/state/systems.query';
import { CustomFieldsV2Service } from 'src/app/company-settings/custom-fields-v2/custom-fields-v2.service';
import { AgentIdToNamePipe } from 'src/app/shared/pipes/agent-id-to-name.pipe';

@Injectable()
export class AiAssessmentsTableCsvHelper {

  constructor(
    private assessmentTemplateNamePipe: AiAssessmentTemplateNamePipe,
    private assessmentStatusPipe: AssessmentStatusPipe,
    private customFieldsV2Service: CustomFieldsV2Service,
    private reviewDateDiffPipe: ReviewDateDiffPipe,
    private assessmentsService: AssessmentsService,
    private employeesService: EmployeesService,
    private systemsQuery: SystemsQuery,
    private agentIdToNamePipe: AgentIdToNamePipe,
  ) { }

  private resolveEntityLabel(entity: AssessmentEntity): string | undefined {
    const id = entity.id?.toString();
    return this.customFieldsV2Service.getOption(id)?.text;
  }

  // v2: an option deleted from settings no longer resolves — drop it from the export instead of
  // emitting the stale id. Cross-functional business unit isn't a v2 option, so keep it.
  private isLiveEntity(entity: AssessmentEntity): boolean {
    const id = entity.id?.toString();
    return !!this.customFieldsV2Service.getOption(id) || id === this.assessmentsService.CROSS_FUNCTIONAL_FUNCTION_NAME;
  }

  async getValueByKey(instance: AssessmentPage, key: AssessmentColumnEnum): Promise<string> {
    try {
      const status = this.assessmentStatusPipe.transform(instance);

      const values = {
        [AssessmentColumnEnum.Creator]: this.agentIdToNamePipe.transform(instance.agentId ?? ''),
        [AssessmentColumnEnum.Name]: instance.name,
        [AssessmentColumnEnum.Type]: this.assessmentTemplateNamePipe.transform(instance.type),
        [AssessmentColumnEnum.Status]: status?.status?.value ?? '',
        [AssessmentColumnEnum.Collaborators]: this.getCollaborators(instance),
        [AssessmentColumnEnum.CollaborationStatus]: instance.collabState,
        [AssessmentColumnEnum.CollabCompleted]: this.getCollabCompleted(instance),
        [AssessmentColumnEnum.Labels]: instance.tags ? instance.tags?.map(({ name }) => name).join(', ') : '',
        [AssessmentColumnEnum.ReviewDate]: instance.reviewDate ? this.reviewDateDiffPipe.transform(instance.reviewDate) : '',
        [AssessmentColumnEnum.CreatedDate]: instance.createdDate ? dayjs(instance.createdDate).format('MMM D, YYYY') : '',
        [AssessmentColumnEnum.LastUpdate]: instance.updatedDate ? dayjs(instance.updatedDate).format('MMM D, YYYY') : '',
        [AssessmentColumnEnum.StatusChangeDate]: instance.stateUpdatedAt ? dayjs(instance.stateUpdatedAt).format('MMM D, YYYY') : '',
      };

      return values.hasOwnProperty(key) ? values[key] : await this.getValueByColumn(instance, key);
    } catch (error) {
      console.error('AiAssessmentsTableCsvHelper.getValueByKey error', { error, key, instanceName: instance?.name });
      return '';
    }
  }

  private getCollabCompleted(instance: AssessmentPage): string {
    const done = instance.collaboratorsDoneCount ?? 0;
    const total = instance.collaboratorsTotalCount ?? 0;
    return `${done}/${total}`;
  }

  private getCollaborators(instance: AssessmentPage): string {
    if (instance.collabState === AssessmentCollaborationState.Submitted) {
      return instance.collabInvites?.filter(({ status }) => status === AssessmentInviteeStatusEnum.Revoked).map(({ email }) => email).join(', ') ?? '';
    }
    return instance.collabInvites?.filter(({ status }) => status === AssessmentInviteeStatusEnum.Invited).map(({ email }) => email).join(', ') ?? '';
  }

  private async getValueByColumn(instance: AssessmentPage, key: AssessmentColumnEnum): Promise<string> {
    try {
      switch (key) {
        case AssessmentColumnEnum.DataSources:
          const dataSources = this.assessmentsService.getRelatedEntitiesByAssessment(instance, AssessmentQuestionTypeEnum.DataSources) as AssessmentResourceId[];
          return dataSources.map(systemId => {
            const system = this.systemsQuery.getEntity(systemId);
            if (system && system.name) {
              return system.name;
            }
            return systemId;
          }).filter(Boolean).join(', ');

        case AssessmentColumnEnum.Owner: {
          if (!instance.ownerId) {
            return '';
          }
          const employee = await firstValueFrom(this.employeesService.getEmployeesByIds([instance.ownerId]));
          if (employee.length > 0) {
            return employee[0].email;
          }
          return '';
        }

        case AssessmentColumnEnum.DataSubjects: {
          const dataSubjects = this.assessmentsService.getRelatedEntitiesByAssessment(instance, AssessmentQuestionTypeEnum.DataSubjects) as AssessmentEntity[];
          return (dataSubjects ?? []).filter(entity => this.isLiveEntity(entity)).map(entity => this.resolveEntityLabel(entity) ?? entity.id as string)
            .filter(Boolean).join(', ');
        }

        case AssessmentColumnEnum.BusinessUnit: {
          const businessUnits = this.assessmentsService.getRelatedEntitiesByAssessment(instance, AssessmentQuestionTypeEnum.BusinessUnit) as AssessmentEntity[];
          return (businessUnits ?? []).filter(entity => this.isLiveEntity(entity)).map(entity => this.resolveEntityLabel(entity) ?? entity.id as string)
            .filter(Boolean).join(', ');
        }

        default: {
          const customQuestions: (AssessmentCustomFieldDropdown | AssessmentCustomFieldText)[] = instance.sections?.flatMap(section => section.questions)
            ?.filter(question => [AssessmentQuestionTypeEnum.DropdownCustomField, AssessmentQuestionTypeEnum.TextCustomField].includes(question.type))
            ?.map(question => question.value as AssessmentCustomFieldDropdown | AssessmentCustomFieldText) ?? [];

          const customField = customQuestions?.find(question => question.customFieldId === key);
          if (customField) {
            return customField['textValue'] ?? customField['options']?.filter(option => option.isSelected)?.map(option => option.label).join(', ') ?? '';
          }
          return '';
        }
      }
    } catch (error) {
      console.error('AiAssessmentsTableCsvHelper.getValueByColumn error', { error, key, instanceName: instance?.name });
      return '';
    }
  }
}
